import { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import LeaderboardPodium from '@/components/leaderboard/LeaderboardPodium';
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable';
import { LeaderboardUser } from '@/types/leaderboard';
import { getWeeklyLeaderboard, getMonthlyLeaderboard, getAllTimeLeaderboard } from '@/services/leaderboardService';
import { Zap, Lightbulb, Clock, Heart, ThumbsUp, Calendar, User, Star, Trophy } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const Leaderboard = () => {
  const [currentTab, setCurrentTab] = useState('week');
  const [showPodium, setShowPodium] = useState(false);
  const [weeklyLeaders, setWeeklyLeaders] = useState<LeaderboardUser[]>([]);
  const [monthlyLeaders, setMonthlyLeaders] = useState<LeaderboardUser[]>([]);
  const [allTimeLeaders, setAllTimeLeaders] = useState<LeaderboardUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isScoreInfoOpen, setIsScoreInfoOpen] = useState(false);
  const { toast } = useToast();
  
  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('Fetching leaderboard data...');
        const [weekly, monthly, allTime] = await Promise.all([
          getWeeklyLeaderboard(),
          getMonthlyLeaderboard(),
          getAllTimeLeaderboard()
        ]);
        
        console.log('Weekly data:', weekly);
        console.log('Monthly data:', monthly);
        console.log('All-time data:', allTime);
        
        setWeeklyLeaders(weekly || []);
        setMonthlyLeaders(monthly || []);
        setAllTimeLeaders(allTime || []);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setError('Failed to load leaderboard data');
        toast({
          title: "Error",
          description: "Failed to load leaderboard data. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboardData();
  }, [toast]);
  
  // Determine which leaderboard data to use based on the current tab
  const getCurrentLeaderboardData = () => {
    switch(currentTab) {
      case 'week': return weeklyLeaders;
      case 'month': return monthlyLeaders;
      case 'allTime': return allTimeLeaders;
      default: return weeklyLeaders;
    }
  };
  
  // Get top 3 users for the podium
  const topThree = getCurrentLeaderboardData().slice(0, 3);
  
  // Animation effect when component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPodium(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleUserClick = (user: LeaderboardUser) => {
    toast({
      title: `${user.name}`,
      description: `Rank #${user.rank} with a score of ${user.score}`,
      duration: 3000,
    });
  };

  // Generate the scoring explanation based on the current tab
  const getScoringExplanation = () => {
    switch(currentTab) {
      case 'week':
        return (
          <div className="flex flex-col gap-3">
            <p className="text-vibebops-textMuted mb-2">Weekly scores are calculated based on your activity over the past 7 days:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                  <ThumbsUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Upvotes</p>
                  <p className="text-sm text-vibebops-textMuted">5 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="font-medium">Likes</p>
                  <p className="text-sm text-vibebops-textMuted">2 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-purple-100 rounded-full p-2">
                  <Zap className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Posts</p>
                  <p className="text-sm text-vibebops-textMuted">2 points each</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'month':
        return (
          <div className="flex flex-col gap-3">
            <p className="text-vibebops-textMuted mb-2">Monthly scores reflect your activity over the past 30 days:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                  <ThumbsUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Upvotes</p>
                  <p className="text-sm text-vibebops-textMuted">5 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="font-medium">Likes</p>
                  <p className="text-sm text-vibebops-textMuted">2 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-purple-100 rounded-full p-2">
                  <Zap className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Posts</p>
                  <p className="text-sm text-vibebops-textMuted">3 points each</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'allTime':
        return (
          <div className="flex flex-col gap-3">
            <p className="text-vibebops-textMuted mb-2">All-time scores showcase your overall contributions and achievements:</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-green-100 rounded-full p-2">
                  <Star className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="font-medium">Experience</p>
                  <p className="text-sm text-vibebops-textMuted">Base points</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                  <ThumbsUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Upvotes</p>
                  <p className="text-sm text-vibebops-textMuted">5 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-pink-100 rounded-full p-2">
                  <Heart className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="font-medium">Likes</p>
                  <p className="text-sm text-vibebops-textMuted">2 points each</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-vibebops-highlight p-3 rounded-lg">
                <div className="flex-shrink-0 bg-amber-100 rounded-full p-2">
                  <User className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium">Followers</p>
                  <p className="text-sm text-vibebops-textMuted">10 points each</p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  
  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Leaderboard</h1>
        <p className="text-vibebops-textMuted">See who's leading the pack with the most engagement and upvotes</p>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibebops-primary" />
        </div>
      ) : error ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <div className="text-center p-4 rounded-lg bg-red-50 text-red-500">
            <p className="font-semibold">Something went wrong</p>
            <p className="text-sm">{error}</p>
            <button 
              className="mt-4 px-4 py-2 bg-vibebops-primary text-white rounded-md"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        <>
          {topThree.length > 0 ? (
            <LeaderboardPodium 
              topThree={topThree} 
              showPodium={showPodium} 
              handleUserClick={handleUserClick} 
            />
          ) : (
            <div className="text-center p-6">
              <p>No data available for the leaderboard yet.</p>
            </div>
          )}
          
          <Tabs 
            defaultValue="week" 
            className="mb-6"
            onValueChange={(value) => setCurrentTab(value)}
          >
            <TabsList className="bg-vibebops-card mb-6">
              <TabsTrigger value="week">This Week</TabsTrigger>
              <TabsTrigger value="month">This Month</TabsTrigger>
              <TabsTrigger value="allTime">All Time</TabsTrigger>
            </TabsList>
            
            <TabsContent value="week">
              <LeaderboardTable users={weeklyLeaders} />
            </TabsContent>
            
            <TabsContent value="month">
              <LeaderboardTable users={monthlyLeaders} />
            </TabsContent>
            
            <TabsContent value="allTime">
              <LeaderboardTable users={allTimeLeaders} />
            </TabsContent>
          </Tabs>

          {/* Scoring Explanation Section */}
          <div className="mt-8 mb-4">
            <Collapsible 
              open={isScoreInfoOpen} 
              onOpenChange={setIsScoreInfoOpen}
              className="vibebops-card p-4 rounded-lg border border-vibebops-border"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-vibebops-accent" />
                  <h3 className="text-lg font-semibold">How Scores Are Calculated</h3>
                </div>
                <CollapsibleTrigger asChild>
                  <button className="rounded-full hover:bg-vibebops-highlight p-1.5 text-vibebops-textMuted hover:text-vibebops-primary transition-colors">
                    {isScoreInfoOpen ? 
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg> : 
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    }
                  </button>
                </CollapsibleTrigger>
              </div>
              
              <CollapsibleContent className="mt-4">
                {getScoringExplanation()}
                
                <div className="mt-6 p-3 bg-vibebops-highlight/50 rounded-lg border border-vibebops-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    <p className="font-medium">Climb the ranks!</p>
                  </div>
                  <p className="text-sm text-vibebops-textMuted">Stay active in the community by creating posts, receiving upvotes and likes, and growing your follower base to improve your position on the leaderboard.</p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </>
      )}
    </MainLayout>
  );
};

export default Leaderboard;
